import React, { FC, useMemo } from 'react';

import pluralize from 'pluralize';

import { useBusinessUnitLabelDisplayName } from 'src/apps/dashboard/helpers/BusinessUnitLabelHelper';
import { useEffectiveFilters } from 'src/apps/dashboard/helpers/FilterHelpers';
import { useBusinessUnitsTableData } from 'src/apps/dashboard/hooks/useBusinessUnitsTableData';
import { useConfigurationFilters } from 'src/apps/dashboard/hooks/useConfigurationFilters';
import { useDashboardDataQuery } from 'src/apps/dashboard/hooks/useDashboardDataQuery';
import { useScoreDataQuery } from 'src/apps/dashboard/hooks/useScoreDataQuery';
import { DashboardTemplate } from 'src/apps/dashboard/templates/DashboardTemplate';
import { isQueryLoading } from 'src/common/helpers/QueryHelpers';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { useDashboardFiltersStore } from 'src/common/stores/dashboardFiltersStore';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { HEADER } from './Dashboard.constants';

export const AccessibilityDashboard: FC = () => {
	usePlatformConfigInitializer();
	const { filters, setFilters } = useDashboardFiltersStore();
	const { isUniquenessServiceEnabled } = usePlatformConfigStore();
	const businessUnitDisplayName = useBusinessUnitLabelDisplayName();

	const { filterDefinitions, filterOptions, labelsExcludedFromDashboard } =
		useConfigurationFilters();
	const effectiveFilters = useEffectiveFilters(filters, filterDefinitions);

	const dashboardDataQuery = useDashboardDataQuery(
		filters,
		labelsExcludedFromDashboard,
		filterDefinitions
	);
	const scoreDataQuery = useScoreDataQuery(filters, labelsExcludedFromDashboard, filterDefinitions);
	const [businessUnitsTableQuery] = useBusinessUnitsTableData({ filters, filterDefinitions });

	const isBusinessUnitsLoading = isQueryLoading(businessUnitsTableQuery);

	const filter = useMemo(
		() => ({ filters, filterDefinitions, filterOptions, onFilterChange: setFilters }),
		[filters, filterDefinitions, filterOptions, setFilters]
	);

	const scoreQuery = useMemo(
		() => ({
			data: scoreDataQuery.data,
			isLoading: scoreDataQuery.isLoading,
			error: scoreDataQuery.error
		}),
		[scoreDataQuery.data, scoreDataQuery.isLoading, scoreDataQuery.error]
	);

	const dashboardQuery = useMemo(
		() => ({
			data: dashboardDataQuery.data,
			isLoading: dashboardDataQuery.isLoading,
			error: dashboardDataQuery.error
		}),
		[dashboardDataQuery.data, dashboardDataQuery.isLoading, dashboardDataQuery.error]
	);

	const bottom = useMemo(
		() => ({
			kind: 'businessUnitsTable' as const,
			title: businessUnitDisplayName ? pluralize(businessUnitDisplayName) : 'Business Units',
			query: {
				data: businessUnitsTableQuery.data,
				isLoading: isBusinessUnitsLoading,
				error: businessUnitsTableQuery.error
			}
		}),
		[
			businessUnitDisplayName,
			businessUnitsTableQuery.data,
			isBusinessUnitsLoading,
			businessUnitsTableQuery.error
		]
	);

	return (
		<DashboardTemplate
			header={HEADER}
			filter={filter}
			effectiveFilters={effectiveFilters}
			scoreQuery={scoreQuery}
			dashboardQuery={dashboardQuery}
			isUniquenessServiceEnabled={isUniquenessServiceEnabled}
			bottom={bottom}
		/>
	);
};
